import { useEffect, useState } from 'react';

import useReportService from '@/services/report';

export const useReportsDashboard = (idEnterprise) => {
    const [isLoading, setIsLoading] = useState(true);
    const [dashboardData, setDashboardData] = useState(null);
    const [filters, setFilters] = useState({
        year: new Date().getFullYear(),
        cycleId: null,
        status: 'all',
        visitPeriod: 'all',
        consultantId: null
    });
    const [tempFilters, setTempFilters] = useState({ ...filters });

    // Reports table pagination
    const [reportsTablePage, setReportsTablePage] = useState(1);
    const [reportsTablePerPage, setReportsTablePerPage] = useState(10);

    // Consultants table pagination
    const [consultantsTablePage, setConsultantsTablePage] = useState(1);
    const [consultantsTablePerPage, setConsultantsTablePerPage] = useState(10);

    const reportService = useReportService();

    const fetchDashboardData = async (filtersToUse, tablePage = 1, tablePerPage = 10) => {
        setIsLoading(true);
        setDashboardData((prev) =>
            prev ? { ...prev, reportsTable: { data: [], total: 0, page: 1, perPage: tablePerPage, lastPage: 1 } } : null
        );
        try {
            const data = await reportService.getDashboard(idEnterprise, {
                year: filtersToUse.year,
                cycleId: filtersToUse.cycleId || null,
                status: filtersToUse.status === 'all' ? null : filtersToUse.status,
                visitPeriod: filtersToUse.visitPeriod === 'all' ? null : filtersToUse.visitPeriod,
                consultantId: filtersToUse.consultantId || null,
                currentUserRole: Number(sessionStorage.getItem('currentUserRole')) || null,
                reportsPage: tablePage,
                reportsPerPage: tablePerPage,
                consultantsPage: consultantsTablePage,
                consultantsPerPage: consultantsTablePerPage
            });
            setDashboardData(data);

            if (!filtersToUse.year && data.years?.length > 0) {
                const newYear = data.years[0];
                setFilters((prev) => ({ ...prev, year: newYear }));
                setTempFilters((prev) => ({ ...prev, year: newYear }));
            }

            // Set first cycle as default if no cycle is selected
            if (!filtersToUse.cycleId && data.cycles?.length > 0) {
                const firstCycleId = data.cycles[0].id;
                setFilters((prev) => ({ ...prev, cycleId: firstCycleId }));
                setTempFilters((prev) => ({ ...prev, cycleId: firstCycleId }));
            }
        } catch (error) {
            console.error('Error fetching dashboard data:', error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchDashboardData(filters, reportsTablePage, reportsTablePerPage);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        idEnterprise,
        filters.year,
        filters.cycleId,
        filters.status,
        filters.visitPeriod,
        filters.consultantId,
        reportsTablePage,
        reportsTablePerPage,
        consultantsTablePage,
        consultantsTablePerPage
    ]);

    const handleTempFilterChange = (field, value) => {
        setTempFilters((prev) => {
            const newFilters = { ...prev, [field]: value };
            if (field === 'status' && value === 'not_visited') {
                newFilters.visitPeriod = 'all';
            }
            return newFilters;
        });
    };

    const handleApplyFilters = () => {
        setReportsTablePage(1);
        setConsultantsTablePage(1);
        setFilters({ ...tempFilters });
        fetchDashboardData(tempFilters, 1, reportsTablePerPage);
    };

    const handleClearFilters = () => {
        const defaultFilters = {
            year: dashboardData?.years?.[0] || new Date().getFullYear(),
            cycleId: dashboardData?.cycles?.[0]?.id || null,
            status: 'all',
            visitPeriod: 'all',
            consultantId: null
        };
        setTempFilters(defaultFilters);
        setFilters(defaultFilters);
        setReportsTablePage(1);
        setConsultantsTablePage(1);
    };

    const handleReportsTablePageChange = (newPage) => {
        setReportsTablePage(newPage);
    };

    const handleReportsTablePerPageChange = (newPerPage) => {
        setReportsTablePerPage(newPerPage);
        setReportsTablePage(1);
    };

    const handleConsultantsTablePageChange = (newPage) => {
        setConsultantsTablePage(newPage);
    };

    const handleConsultantsTablePerPageChange = (newPerPage) => {
        setConsultantsTablePerPage(newPerPage);
        setConsultantsTablePage(1);
    };

    const cards = dashboardData?.cards || {};
    const years = dashboardData?.years || [new Date().getFullYear()];
    const cycles = dashboardData?.cycles || [];
    const consultants = dashboardData?.consultants || [];
    const reportsTable = dashboardData?.reportsTable || { data: [], total: 0, page: 1, perPage: 10, lastPage: 1 };
    const consultantsTable = dashboardData?.consultantsTable || { data: [], total: 0, page: 1, perPage: 10, lastPage: 1 };

    return {
        isLoading,
        dashboardData,
        filters,
        tempFilters,
        cards,
        years,
        cycles,
        consultants,
        reportsTable,
        reportsTablePage,
        reportsTablePerPage,
        consultantsTable,
        consultantsTablePage,
        consultantsTablePerPage,
        handleTempFilterChange,
        handleApplyFilters,
        handleClearFilters,
        handleReportsTablePageChange,
        handleReportsTablePerPageChange,
        handleConsultantsTablePageChange,
        handleConsultantsTablePerPageChange
    };
};
